import React, { useMemo } from "react";
import styled from "styled-components";
import SkeletonPMCLinkedRegForm from "../../../loaders/SkeletonPMCLinkedRegForm";
import PMCYearAccordion from "./PMCYearAccordion";
import { SectionTitle, SectionDescription } from "./application/styles/primitives";

const OverviewContainer = styled.div`
  padding:  0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OverviewHeader = styled.div`
  margin-bottom: 0;
`;

const QualificationCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  margin-bottom: 0;
  box-sizing: border-box;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
  }
`;

const QualificationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  @media (max-width: 768px) {
    width: 100%;
  }
  @media (max-width: 480px) {
    flex-wrap: wrap;
    row-gap: 6px;
  }
`;

const QualificationIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  color: var(--topbar-color);
  background-color: color-mix(in srgb, var(--topbar-color) 20%, var(--white));
  display: flex;
    width: 25px;
    height: 25px;
    justify-content: center;
    align-items: center;
  font-size: 12px;
  border-radius: 2px;
  overflow: hidden;
`;

const QualificationName = styled.span`
font-family: "Segoe UI";
font-size: 14px;
font-style: normal;
font-weight: bold;
line-height: 14px;
color: var(--lightGray3);
word-break: break-word;
flex: 1;
min-width: 0;
`;
const CompletionBadge = styled.div`
display: flex;
align-items: center;
gap: 5px;
align-self: center;
flex-shrink: 0;
@media (max-width: 768px) {
  align-self: flex-start;
  width: 100%;
}
@media (max-width: 480px) {
  flex-wrap: wrap;
  justify-content: flex-start;
  row-gap: 6px;
}
`
const CompletionIcon = styled.i`
color: #5CB85C;
font-size: 16px;
font-style: normal;
font-weight: 900;
line-height: 16px;
`;
const CompletionText = styled.span`
color: #223534;
font-family: "Segoe UI";
font-size: 14px;
font-style: normal;
font-weight: 600;
line-height: 14px;
`;

const RegularText = styled.span`
font-weight: 400;
`

const ModuleOverviewItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

function PMCQualificationOverview({ qualification, modules, isLoading }) {
  // Group modules by year
  const groupedModules = useMemo(() => {
    return Object.values(modules).reduce((acc, module) => {
      const year = module.year || "No Year";
      if (!acc[year]) acc[year] = [];
      acc[year].push(module);
      acc[year].sort((a, b) => (a.term || 0) - (b.term || 0));
      return acc;
    }, {});
  }, [modules]);

  // Calculate total completion
  const completionStats = useMemo(() => {
    const moduleArray = Object.values(modules);
    const total = moduleArray.length;
    const completed = moduleArray.filter(
      (m) => m.status === "PASS" || m.status === "EXEMPTED" || m.status === "EXEMPT"
    ).length;
    return { completed, total };
  }, [modules]);

  if (isLoading) {
    return (
      <OverviewContainer>
        <SkeletonPMCLinkedRegForm />
      </OverviewContainer>
    );
  }

  return (
    <OverviewContainer>
      <OverviewHeader>
        <SectionTitle>Your Current Enrolment Overview</SectionTitle>
        <SectionDescription>
          Please review your current enrolment to help you with completing your re-registration.
        </SectionDescription>
      </OverviewHeader>

      {qualification && (
        <QualificationCard>
          <QualificationInfo>
            <QualificationIcon>
              <i className="fa-solid fa-graduation-cap"></i>
            </QualificationIcon>
            <QualificationName>{qualification?.name}</QualificationName>
          </QualificationInfo>
          <CompletionBadge>
            <CompletionIcon className="fa-light fa-check-circle"></CompletionIcon>
            <CompletionText>
              Completed: <RegularText>{completionStats.completed}/{completionStats.total}</RegularText>
            </CompletionText>
          </CompletionBadge>
        </QualificationCard>
      )}

      <ModuleOverviewItems>
        {Object.keys(groupedModules).map((year) => (
          <PMCYearAccordion
            key={year}
            year={year}
            modules={groupedModules[year]}
            isOverviewTab={true}
          />
        ))}
      </ModuleOverviewItems>
    </OverviewContainer>
  );
}

export default PMCQualificationOverview;
